<template>
    <div class="my-location">
        <!-- Header -->
        <div class="my-location__header">
            <h2 class="my-location__title">Mijn vestiging</h2>
        </div>

        <!-- Body: two columns -->
        <div class="my-location__body" v-if="location">
            <!-- Left column -->
            <div class="my-location__col">
                <dl class="my-location__field">
                    <dt class="my-location__label">Vestiging</dt>
                    <dd class="my-location__value">{{ location.name }}</dd>
                </dl>

                <div class="my-location__group">
                    <dl class="my-location__field">
                        <dt class="my-location__label">Adres</dt>
                        <dd class="my-location__value">
                            {{ location.street }}<br>{{ location.postal_city }}
                        </dd>
                    </dl>

                    <a
                        v-if="location.maps_url"
                        :href="location.maps_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="my-location__link"
                    >
                        Routebeschrijving
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                        </svg>
                    </a>
                </div>
            </div>

            <!-- Right column -->
            <div class="my-location__col">
                <dl v-if="location.email" class="my-location__field">
                    <dt class="my-location__label">E-mailadres</dt>
                    <dd class="my-location__value">{{ location.email }}</dd>
                </dl>

                <dl v-if="location.phone" class="my-location__field">
                    <dt class="my-location__label">Telefoonnummer</dt>
                    <dd class="my-location__value">{{ location.phone }}</dd>
                </dl>

                <!-- Opening hours widget -->
                <div v-if="hoursToday" class="my-location__hours">
                    <button
                        class="my-location__hours-toggle"
                        :aria-expanded="hoursOpen ? 'true' : 'false'"
                        @click="hoursOpen = !hoursOpen"
                    >
                        <span class="my-location__hours-today">
                            <span class="my-location__hours-dot"></span>
                            Vandaag
                        </span>
                        <span class="my-location__hours-time">
                            {{ todayLabel }}
                            <svg
                                class="my-location__hours-chevron"
                                :class="{ 'my-location__hours-chevron--open': hoursOpen }"
                                width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"
                            >
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                            </svg>
                        </span>
                    </button>

                    <ul v-if="hoursOpen" class="my-location__hours-list">
                        <li
                            v-for="day in location.hours"
                            :key="day.day"
                            class="my-location__hours-item"
                            :class="{ 'my-location__hours-item--today': day.is_today }"
                        >
                            <span class="my-location__hours-dayname">{{ day.day_name }}</span>
                            <span class="my-location__hours-slots">
                                <template v-if="day.slots.length">
                                    <span v-for="(slot, i) in day.slots" :key="i">
                                        {{ slot.open }} – {{ slot.close }}<template v-if="i < day.slots.length - 1">, </template>
                                    </span>
                                </template>
                                <template v-else>Gesloten</template>
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- No location fallback -->
        <div class="my-location__body" v-else>
            <div class="my-location__empty">
                Kies je favoriete vestiging.
            </div>
        </div>

        <hr class="my-location__divider">

        <!-- Footer -->
        <div class="my-location__footer">
            <button class="my-location__edit-btn" type="button" @click="openModal">
                {{ location ? 'Bewerken' : 'Kies vestiging' }}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
            </button>
        </div>
    </div>
</template>

<script>
import LocationModal from "./LocationModal";

export default {
    name: 'MyLocation',

    data() {
        return {
            location: null,
            hoursOpen: false,
        };
    },

    computed: {
        hoursToday() {
            return this.location?.hours?.find(d => d.is_today) ?? null;
        },

        todayLabel() {
            const today = this.hoursToday;
            if (!today || !today.slots.length) return 'Gesloten';
            return today.slots.map(s => `${s.open} tot ${s.close}`).join(', ');
        },
    },

    mounted() {
        this.fetchData();
    },

    methods: {
        fetchData() {
            console.log('fetching data');

            axios.get('/api/customer/my-location')
                .then(({ data }) => {
                    this.location = data;
                })
                .catch(() => {});
        },

        openModal() {
            this.$modal.show(LocationModal, {
                // Props
                props: {
                    onLocationUpdated: () => {
                        console.log('Location updated callback triggered');
                        this.fetchData();
                    }
                },
                // Options
                classes: ['location-update-modal', 'small-modal', 'no-scrollbar'],
            });
        }
    }
}
</script>

<style scoped>
.my-location {
    background: #ffffff;
    border-radius: 5px;
    box-shadow: 0 8px 5px rgba(0, 125, 147, 0.13);
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
}

/* Header */
.my-location__header {
    display: flex;
    align-items: center;
    gap: 8px;
}

.my-location__header-icon {
    color: #033239;
    flex-shrink: 0;
}

.my-location__title {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 28px;
    color: #00abc8;
    margin: 0;
}

/* Body */
.my-location__body {
    display: flex;
    gap: 154px;
    align-items: flex-start;
}

@media (max-width: 767px) {
    .my-location__body {
        flex-direction: column;
        gap: 24px;
    }
}

.my-location__empty {
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: #033239;
}

.my-location__col {
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex-shrink: 0;
}

.my-location__group {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* Fields */
.my-location__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;
}

.my-location__label {
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
    color: rgba(0, 171, 200, 0.85);
    white-space: nowrap;
}

.my-location__value {
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: #033239;
    margin: 0;
    white-space: nowrap;
}

/* Routebeschrijving link */
.my-location__link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 16px;
    color: #033239;
    text-decoration: none;
}

.my-location__link:hover,
.my-location__link:focus {
    color: #00abc8;
    text-decoration: none;
}

/* Opening hours widget */
.my-location__hours {
    background: rgba(0, 171, 200, 0.1);
    border-radius: 8px;
    overflow: hidden;
    width: 332px;
    max-width: 100%;
}

.my-location__hours-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: none;
    border: none;
    cursor: pointer;
    font-family: 'Open Sans', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #033239;
    text-decoration: none;
}

.my-location__hours-today {
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

.my-location__hours-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #82db39;
    flex-shrink: 0;
}

.my-location__hours-time {
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

.my-location__hours-chevron {
    transition: transform 0.2s ease;
}

.my-location__hours-chevron--open {
    transform: rotate(180deg);
}

/* Expanded hours list */
.my-location__hours-list {
    list-style: none;
    margin: 0;
    padding: 0 16px 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.my-location__hours-item {
    display: flex;
    justify-content: space-between;
    font-family: 'Open Sans', sans-serif;
    font-size: 13px;
    line-height: 20px;
    color: #033239;
}

.my-location__hours-item--today {
    font-weight: 700;
}

.my-location__hours-dayname {
    width: 90px;
}

/* Divider */
.my-location__divider {
    border: none;
    border-top: 1px solid #cdd6d7;
    margin: 0;
}

/* Footer */
.my-location__footer {
    display: flex;
    justify-content: flex-end;
}

.my-location__edit-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 16px;
    color: #033239;
    background: #82db39;
    border: 1px solid #82db39;
    border-radius: 4px;
    padding: 16px 24px;
    cursor: pointer;
    opacity: 1;
    text-decoration: none;
}
</style>
